import type { Language } from '../contexts/I18nContext';
import type { Theme } from '../types';

export interface UserPreferences {
  language: Language;
  theme: Theme;
  dateFormat?: string;
  timezone?: string;
}

export interface PreferenceServiceConfig {
  storageKey: string;
  defaultPreferences: UserPreferences;
}

class PreferenceService {
  private storageKey: string;
  private defaultPreferences: UserPreferences;
  private cache: UserPreferences | null = null;

  constructor(config: PreferenceServiceConfig) {
    this.storageKey = config.storageKey;
    this.defaultPreferences = config.defaultPreferences;
  }

  /**
   * Load preferences from localStorage with fallback handling
   */
  loadPreferences(): UserPreferences {
    try {
      // Return cached preferences if available
      if (this.cache) {
        return this.cache;
      }

      const stored = localStorage.getItem(this.storageKey);
      
      if (!stored) {
        // No stored preferences, use defaults
        this.cache = { ...this.defaultPreferences };
        return this.cache;
      }

      const parsed = JSON.parse(stored);
      
      // Validate and merge with defaults to handle missing or invalid properties
      const preferences = this.validateAndMergePreferences(parsed);
      
      this.cache = preferences;
      return preferences;
    } catch (error) {
      console.warn('Failed to load preferences from localStorage:', error);
      // Return defaults on any error
      this.cache = { ...this.defaultPreferences };
      return this.cache;
    }
  }

  /**
   * Save preferences to localStorage
   */
  savePreferences(preferences: UserPreferences): boolean {
    try {
      const validatedPreferences = this.validateAndMergePreferences(preferences);
      localStorage.setItem(this.storageKey, JSON.stringify(validatedPreferences));
      this.cache = validatedPreferences;
      return true;
    } catch (error) {
      console.warn('Failed to save preferences to localStorage:', error);
      return false;
    }
  }

  /**
   * Update specific preference properties
   */
  updatePreferences(updates: Partial<UserPreferences>): UserPreferences {
    const current = this.loadPreferences();
    const updated = { ...current, ...updates };
    const validated = this.validateAndMergePreferences(updated);
    
    this.savePreferences(validated);
    return validated;
  }

  /**
   * Reset preferences to defaults
   */
  resetPreferences(): UserPreferences {
    try {
      localStorage.removeItem(this.storageKey);
      this.cache = { ...this.defaultPreferences };
      return this.cache;
    } catch (error) {
      console.warn('Failed to reset preferences:', error);
      this.cache = { ...this.defaultPreferences };
      return this.cache;
    }
  }

  /**
   * Get a specific preference value with fallback
   */
  getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] {
    const preferences = this.loadPreferences();
    return preferences[key];
  }

  /**
   * Set a specific preference value
   */
  setPreference<K extends keyof UserPreferences>(
    key: K, 
    value: UserPreferences[K]
  ): UserPreferences {
    return this.updatePreferences({ [key]: value } as Partial<UserPreferences>);
  }

  /**
   * Check if localStorage is available
   */
  isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Validate preferences object and merge with defaults
   */
  private validateAndMergePreferences(preferences: any): UserPreferences {
    const validated: UserPreferences = { ...this.defaultPreferences };

    // Validate language
    if (preferences.language && ['en', 'ar'].includes(preferences.language)) {
      validated.language = preferences.language;
    }

    // Validate theme
    if (preferences.theme && ['light', 'dark'].includes(preferences.theme)) {
      validated.theme = preferences.theme;
    }

    // Validate optional fields
    if (preferences.dateFormat && typeof preferences.dateFormat === 'string') {
      validated.dateFormat = preferences.dateFormat;
    }

    if (preferences.timezone && typeof preferences.timezone === 'string') {
      validated.timezone = preferences.timezone;
    }

    return validated;
  }

  /**
   * Clear cache (useful for testing or when preferences change externally)
   */
  clearCache(): void {
    this.cache = null;
  }
}

// Default preferences configuration
const defaultPreferences: UserPreferences = {
  language: 'ar',
  theme: 'light',
  dateFormat: 'DD/MM/YYYY',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
};

// Create and export the preference service instance
export const preferenceService = new PreferenceService({
  storageKey: 'holiday-dashboard-i18n-theme-preferences',
  defaultPreferences,
});

// Export the class for testing
export { PreferenceService };